import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
    FiArrowLeft, FiPlus, FiTrash2, FiEdit2, FiChevronDown, FiChevronUp, 
    FiBook, FiLayers, FiAlertCircle, FiMessageSquare 
} from 'react-icons/fi';
import { API_BASE } from '../../../api';
import { ENDPOINTS as LMS_ENDPOINTS } from '../Utils/Endpoints';
import { Models as LmsModels } from '../Utils/ModelsLMS';
import ConfirmDeleteDialog from '../../security/ConfirmDeleteDialog';
import SemestreCursoTab from '../Components/Tabs/SemestreCursoTab';
import AlertDialog from '../Components/Shared/AlertDialog';
import SolicitarRevisionCarreraModal from '../Components/SolicitarRevisionCarreraModal';
import ObservacionesCarreraModal from '../Modals/ObservacionesCarreraModal';
import { useLogos } from '../../../context/LogoProvider';
import '../Styles/LmsExtra.css';

const CarreraCurriculum = () => {
    const { id: idCarrera } = useParams();
    const navigate = useNavigate();
    const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
    const { getSystemConfig } = useLogos();

    // Estados
    const [carrera, setCarrera] = useState(null);
    const [semestres, setSemestres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modales y UI
    const [expandedSemesters, setExpandedSemesters] = useState({});
    const [isAddSemesterModalOpen, setIsAddSemesterModalOpen] = useState(false);
    const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);
    const [isPublishAlertOpen, setIsPublishAlertOpen] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [currentSemesterId, setCurrentSemesterId] = useState(null);
    const [availableCourses, setAvailableCourses] = useState([]);
    const [searchCourse, setSearchCourse] = useState('');

    // Datos para formularios
    const [semesterName, setSemesterName] = useState('');
    const [semesterDescription, setSemesterDescription] = useState('');
    const [classicEditingSemesterId, setClassicEditingSemesterId] = useState(null); // Para el modal clásico
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null, name: '' });
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });
    const [isSolicitarModalOpen, setIsSolicitarModalOpen] = useState(false);
    const [isObservacionesModalOpen, setIsObservacionesModalOpen] = useState(false);

    // Edición en línea
    const [editingCarreraTitle, setEditingCarreraTitle] = useState(false);
    const [carreraTitleDraft, setCarreraTitleDraft] = useState('');
    const [editingSemestreId, setEditingSemestreId] = useState(null); // Para edición en línea del título
    const [semestreTitleDraft, setSemestreTitleDraft] = useState('');

    // Configuración de Carrera (Inline Modal state)
    const [isCarreraModalOpen, setIsCarreraModalOpen] = useState(false);
    const [careerConfigDraft, setCareerConfigDraft] = useState({
        nombre: '',
        descripcion: '',
        urlImagen: ''
    });
    const [configImageUploading, setConfigImageUploading] = useState(false);
    const [configImageError, setConfigImageError] = useState('');

    const hasCertificate = !!carrera?.idCertificado;

    const handleCarreraTitleKeyDown = (e) => {
        if (e.key === 'Enter') saveCarreraTitle();
        if (e.key === 'Escape') {
            setEditingCarreraTitle(false);
            setCarreraTitleDraft(carrera.nombre);
        }
    };

    const handleSemestreTitleKeyDown = (e, idS) => {
        if (e.key === 'Enter') saveSemestreTitle(idS);
        if (e.key === 'Escape') setEditingSemestreId(null);
    };

    // Cargar datos iniciales
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Obtener Carrera
            const resCarrera = await axios.get(LMS_ENDPOINTS.carrerasById(idCarrera));
            const carreraData = Array.isArray(resCarrera.data) ? resCarrera.data[0] : resCarrera.data;
            if (carreraData) {
                const norm = LmsModels.carrera(carreraData);
                setCarrera(norm);
                setCarreraTitleDraft(norm.nombre);
            }

            // 2. Obtener Catálogos
            const [resNiveles, resHabilidades] = await Promise.all([
                axios.get(LMS_ENDPOINTS.catNivel),
                axios.get(LMS_ENDPOINTS.catTipoHabilidad)
            ]);
            const niveles = (resNiveles.data || []).map(LmsModels.niveles);
            const habilidades = (resHabilidades.data || []).map(LmsModels.tipoHabilidad);

            // 3. Obtener Semestres (Filtrados por Carrera)
            const resSemestres = await axios.get(LMS_ENDPOINTS.semestres);
            const semestresFiltrados = (resSemestres.data || [])
                .map(LmsModels.semestre)
                .filter(s => Number(s.idCarrera) === Number(idCarrera) && s.activo)
                .sort((a, b) => (a.idSemestre) - (b.idSemestre));

            const resSemestreCursos = await axios.get(LMS_ENDPOINTS.semestreCurso);
            const semCursosData = (resSemestreCursos.data || []).map(LmsModels.semestreCurso);

            const resTodosCursos = await axios.get(LMS_ENDPOINTS.cursos);
            const todosCursos = (resTodosCursos.data || []).map(LmsModels.cursos);

            const semestresMapeados = semestresFiltrados.map(s => {
                const idS = s.idSemestre;
                return {
                    ...s,
                    id: idS,
                    nombre: s.nombre,
                    cursos: semCursosData
                        .filter(sc => Number(sc.idSemestre) === Number(idS) && (sc.activo))
                        .map(sc => {
                            const cursoInfo = todosCursos.find(c => Number(c.idCurso) === Number(sc.idCurso));
                            if (!cursoInfo) return null;

                            const nivel = niveles.find(n => n.idNivel === cursoInfo.idNivel);
                            const habilidad = habilidades.find(h => h.idTipoHabilidad === cursoInfo.idTipoHabilidad);

                            return {
                                idSemestreCurso: sc.idSemestreCurso,
                                idCurso: sc.idCurso,
                                nombre: cursoInfo.nombre,
                                imagen: cursoInfo.uRLRutaImagen,
                                nivel: nivel ? nivel.nombre : '-',
                                habilidad: habilidad ? habilidad.nombre : '-',
                                duracion: cursoInfo.duracion
                            };
                        }).filter(Boolean)
                };
            });

            setSemestres(semestresMapeados);

            // Solo inicializar expandidos si no estaban ya definidos para no perder el estado del usuario
            setExpandedSemesters(prev => {
                const next = { ...prev };
                semestresMapeados.forEach(s => {
                    if (next[s.id] === undefined) next[s.id] = true;
                });
                return next;
            });

            setAvailableCourses(todosCursos.filter(c => c.activo && c.terminado));

        } catch (err) {
            console.error('Error al cargar currículo de carrera:', err);
            setError('No se pudo cargar la información de la carrera.');
        } finally {
            setLoading(false);
        }
    }, [idCarrera, SERVER_URL]);

    useEffect(() => {
        loadData();
    }, [idCarrera, loadData]);

    // Handlers para Títulos (Inline)
    const saveCarreraTitle = async () => {
        if (!carreraTitleDraft.trim() || carreraTitleDraft === carrera.nombre) {
            setEditingCarreraTitle(false);
            setCarreraTitleDraft(carrera.nombre);
            return;
        }
        try {
            const dto = { ...carrera, nombre: carreraTitleDraft };
            await axios.put(LMS_ENDPOINTS.carreras, dto);
            setCarrera(dto);
            setEditingCarreraTitle(false);
        } catch (error) {
            console.error('Error al actualizar carrera:', error);
            setEditingCarreraTitle(false);
        }
    };

    const saveSemestreTitle = async (idS) => {
        if (!semestreTitleDraft.trim()) {
            setEditingSemestreId(null);
            return;
        }
        try {
            const current = semestres.find(s => s.id === idS);
            if (semestreTitleDraft === current.nombre) {
                setEditingSemestreId(null);
                return;
            }
            const dto = {
                idSemestre: idS,
                idCarrera: Number(idCarrera),
                nombre: semestreTitleDraft,
                activo: current ? (current.activo || true) : true
            };
            await axios.put(LMS_ENDPOINTS.semestres, dto);
            setSemestres(prev => prev.map(s => s.id === idS ? { ...s, nombre: semestreTitleDraft } : s));
            setEditingSemestreId(null);
        } catch (error) {
            console.error('Error al actualizar semestre:', error);
            setEditingSemestreId(null);
        }
    };

    // Handlers para Semestres (Classic Modal / CRUD)
    const handleAutoAddSemester = async () => {
        try {
            const nextIndex = semestres.length + 1;
            const dto = {
                idCarrera: Number(idCarrera),
                nombre: `Nivel ${nextIndex}`,
                descripcion: '',
                activo: true
            };
            await axios.post(LMS_ENDPOINTS.semestres, dto);
            loadData();
        } catch (error) {
            console.error('Error al crear semestre automático:', error);
        }
    };

    const handleUpdateSemesterClassic = async () => {
        if (!semesterName.trim() || !classicEditingSemesterId) return;
        try {
            const current = semestres.find(s => s.id === classicEditingSemesterId);
            const dto = {
                idSemestre: classicEditingSemesterId,
                idCarrera: Number(idCarrera),
                nombre: semesterName,
                descripcion: semesterDescription,
                activo: current ? (current.activo || true) : true
            };
            await axios.put(LMS_ENDPOINTS.semestres, dto);
            setSemesterName('');
            setSemesterDescription('');
            setClassicEditingSemesterId(null);
            setIsAddSemesterModalOpen(false);
            loadData();
        } catch (error) {
            console.error('Error al actualizar semestre:', error);
        }
    };

    // Cargar datos al abrir modal de configuración
    useEffect(() => {
        if (isCarreraModalOpen && carrera) {
            setCareerConfigDraft({
                nombre: carrera.nombre || '',
                descripcion: carrera.descripcion || '',
                urlImagen: carrera.urlImagen || ''
            });
            setConfigImageError('');
        }
    }, [isCarreraModalOpen, carrera]);

    const handleConfigImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validaciones básicas
        if (!file.type.startsWith('image/')) {
            setConfigImageError('Solo se permiten archivos de imagen.');
            return;
        }

        const maxMB = 5; // Configuración hardcodeada o desde contexto
        if (file.size > maxMB * 1024 * 1024) {
            setConfigImageError(`La imagen no debe superar los ${maxMB}MB.`);
            return;
        }

        setConfigImageError("");

        try {
            setConfigImageUploading(true);
            const formData = new FormData();
            formData.append('file', file);

            const COURSE_IMAGE_UPLOAD_ENDPOINT = `${API_BASE}/upload/carrera-imagen`; // Endpoint específico para carreras
            const response = await fetch(COURSE_IMAGE_UPLOAD_ENDPOINT, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Error al subir la imagen');

            const data = await response.json();
            if (!data.filePath) throw new Error('No se recibió la ruta del archivo');

            setCareerConfigDraft(prev => ({
                ...prev,
                urlImagen: data.filePath
            }));
        } catch (error) {
            console.error('Error subiendo imagen:', error);
            setConfigImageError('Error al subir la imagen al servidor.');
        } finally {
            setConfigImageUploading(false);
        }
    };

    const handleSaveCareerConfig = async () => {
        try {
            // El backend espera el objeto completo con PascalCase (o lo que venga en 'carrera' si coincide)
            // y el ID dentro del cuerpo, no en la URL.
            const formData = {
                ...carrera,
                // Aseguramos que el ID vaya, aunque venga en 'carrera'
                IdCarrera: idCarrera,
                nombre: careerConfigDraft.nombre,
                descripcion: careerConfigDraft.descripcion,
                urlImagen: careerConfigDraft.urlImagen
            };

            await axios.put(LMS_ENDPOINTS.carreras, formData);
            setAlertDialog({
                open: true,
                title: "Éxito",
                message: "Carrera actualizada correctamente.",
                variant: "success",
            });
            setIsCarreraModalOpen(false);
            loadData();
        } catch (error) {
            console.error('Error al guardar carrera:', error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "Error al guardar cambios en la carrera.",
                variant: "error",
            });
        }
    };

    const handlePrePublishValidation = () => {
        if (semestres.length === 0) {
            setAlertDialog({
                open: true,
                title: "Carrera vacía",
                message: "La carrera no tiene niveles. Agrega al menos un nivel antes de continuar.",
                variant: "warning"
            });
            return;
        }
        
        const semestresVacios = semestres.filter(s => s.cursos.length === 0);
        
        if (semestresVacios.length > 0) {
            setAlertDialog({
                open: true,
                title: "Niveles sin cursos",
                message: (
                    <div>
                        <p>No se puede continuar. Los siguientes niveles no tienen cursos asignados:</p>
                        <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                            {semestresVacios.map(s => <li key={s.id}>{s.nombre}</li>)}
                        </ul>
                        <p>Por favor agrega al menos un curso a cada nivel.</p>
                    </div>
                ),
                variant: "error"
            });
            return;
        }
        
        if (hasCertificate) {
            setIsPublishAlertOpen(true);
        } else {
            setIsSolicitarModalOpen(true);
        }
    };

    const handlePublishCarrera = async () => {
        try {
            setIsPublishing(true);

            let finalCertId = carrera.idCertificado;

            // 1. Solo generamos si no tiene certificado previo
            if (!finalCertId) {
                // Obtener firma del director (necesaria para el ID)
                const resFirmas = await axios.get(LMS_ENDPOINTS.firmas);
                const firmas = resFirmas.data || [];
                // Buscar firma activa de director
                const directorFirma = firmas.find(f => (f.esDirector || f.EsDirector) && (f.activo || f.Activo));

                if (!directorFirma) throw new Error("No se encontró una firma activa de Director para generar el certificado.");

                // Llamar al backend para generar el certificado
                const idFirmaFinal = directorFirma.idFirma || directorFirma.IdFirma;
                
                const generatePayload = {
                    IdCarrera: Number(idCarrera),
                    IdFirma: Number(idFirmaFinal)
                };

                const generateRes = await axios.post(`${LMS_ENDPOINTS.certificadoCarrera}/Generate`, generatePayload, {
                    timeout: 30000 
                });

                finalCertId = generateRes.data;
                if (!finalCertId) throw new Error("El servidor no devolvió un ID de certificado válido.");
            }

            // 2. Actualizar Carrera para publicarla
            const carreraDto = {
                idCarrera: carrera.idCarrera,
                nombre: carrera.nombre,
                descripcion: carrera.descripcion,
                urlImagen: carrera.urlImagen,
                idInstructor: carrera.idInstructor,
                activado: carrera.activado,
                publicado: true,
                idCertificado: finalCertId 
            };

            await axios.put(LMS_ENDPOINTS.carreras, carreraDto);

            setCarrera(prev => ({ ...prev, publicado: true, idCertificado: finalCertId }));
            setIsPublishAlertOpen(false);
            setAlertDialog({
                open: true,
                title: "Éxito",
                message: "Carrera publicada exitosamente.",
                variant: "success",
            });

        } catch (error) {
            console.error("Error en publicación de carrera:", error);
            const responseData = error.response?.data;
            const msg = typeof responseData === 'string' ? responseData : (responseData?.message || error.message || "Error desconocido");
            
            console.error("[handlePublishCarrera] Detalle del error del servidor:", responseData);

            setAlertDialog({
                open: true,
                title: "Error al publicar",
                message: msg,
                variant: "error",
            });
        } finally {
            setIsPublishing(false);
        }
    };

    const confirmDeleteSemester = async () => {
        try {
            // 1. Desactivar todos los cursos asociados a este nivel
            const targetSemestre = semestres.find(s => s.id === deleteDialog.id);
            if (targetSemestre && targetSemestre.cursos && targetSemestre.cursos.length > 0) {
                const deletePromises = targetSemestre.cursos.map(curso =>
                    axios.delete(LMS_ENDPOINTS.semestreCursoById(curso.idSemestreCurso))
                );
                await Promise.all(deletePromises);
            }

            // 2. Desactivar el nivel/semestre (soft-delete vía PUT)
            const dto = {
                idSemestre: deleteDialog.id,
                idCarrera: Number(idCarrera),
                nombre: deleteDialog.name,
                activo: false
            };
            await axios.put(LMS_ENDPOINTS.semestres, dto);

            setDeleteDialog({ open: false, type: '', id: null, name: '' });
            loadData();
        } catch (error) {
            console.error('Error al eliminar semestre:', error);
            setAlertDialog({
                open: true,
                title: "Error al eliminar nivel",
                message: "No se pudo eliminar el nivel. Por favor, intente de nuevo.",
                variant: "error"
            });
        }
    };

    // Handlers para Cursos
    const handleAddCourseToSemester = async (idCurso) => {
        try {
            const dto = {
                idSemestre: currentSemesterId,
                idCurso: idCurso,
                activo: true
            };
            await axios.post(LMS_ENDPOINTS.semestreCurso, dto);
            loadData();
            setIsAddCourseModalOpen(false);
        } catch (error) {
            console.error('Error al agregar curso al semestre:', error);
        }
    };

    const confirmDeleteCourse = async () => {
        try {
            await axios.delete(LMS_ENDPOINTS.semestreCursoById(deleteDialog.id));
            setDeleteDialog({ open: false, type: '', id: null, name: '' });
            loadData();
        } catch (error) {
            console.error('Error al eliminar curso del semestre:', error);
            setAlertDialog({
                open: true,
                title: "Error al quitar curso",
                message: "No se pudo quitar el curso del nivel. Por favor, intente de nuevo.",
                variant: "error"
            });
        }
    };

    const toggleSemester = (id) => {
        setExpandedSemesters(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Filtrar cursos disponibles que no estén ya en el semestre actual
    const filteredAvailableCourses = useMemo(() => {
        if (!currentSemesterId) return [];

        // Obtenemos todos los IDs de cursos ya asignados a CUALQUIER semestre de esta carrera
        const allAssignedIdsInCareer = semestres.reduce((acc, s) => {
            const courseIds = (s.cursos || []).map(c => c.idCurso);
            return [...acc, ...courseIds];
        }, []);

        return availableCourses.filter(c =>
            !allAssignedIdsInCareer.includes(c.idCurso) &&
            (c.nombre || '').toLowerCase().includes(searchCourse.toLowerCase())
        );
    }, [availableCourses, currentSemesterId, semestres, searchCourse]);

    if (loading) return <div className="cc-page">Cargando currículo de la carrera...</div>;
    if (error) return <div className="cc-page"><p style={{ color: '#dc2626' }}>{error}</p></div>;

    return (
        <div className="cc-page">
            {/* Breadcrumb */}
            <div className="cc-breadcrumb">
                <button
                    type="button"
                    className="cc-breadcrumb-back"
                    onClick={() => navigate("/menu/lms/administrar-carreras")}
                >
                    <FiArrowLeft />
                    Volver a Administrar Carreras
                </button>
            </div>

            <div className="cc-layout">
                {/* MAIN */}
                <div className="cc-main">
                    {/* Título de carrera editable */}
                    {editingCarreraTitle && !carrera.publicado ? (
                        <input
                            className="cc-inline-input cc-inline-input-course"
                            value={carreraTitleDraft}
                            onChange={(e) => setCarreraTitleDraft(e.target.value)}
                            onBlur={saveCarreraTitle}
                            onKeyDown={handleCarreraTitleKeyDown}
                            autoFocus
                        />
                    ) : (
                        <h1
                            className="cc-course-title"
                            onDoubleClick={() => {
                                if (carrera.publicado) return;
                                setCarreraTitleDraft(carrera?.nombre || '');
                                setEditingCarreraTitle(true);
                            }}
                            title={carrera.publicado ? "Carrera publicada - Edición bloqueada" : "Doble clic para editar el nombre de la carrera"}
                            style={{ cursor: carrera.publicado ? 'default' : 'text', color: 'var(--color-primary-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            {carrera?.nombre || carrera?.Nombre}
                            {carrera.publicado && (
                                <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '1.2rem', marginLeft: '8px' }}>
                                    <span style={{ fontSize: '1.4rem' }}>✓</span> Publicado
                                </span>
                            )}
                        </h1>
                    )}

                    {/* Chips resumen */}
                    <div className="cc-chip-row">
                        <span className="cc-chip">
                            {semestres.length} niveles
                        </span>
                        <span className="cc-chip">
                            {semestres.reduce((acc, s) => acc + s.cursos.length, 0)} cursos totales
                        </span>
                    </div>

                    {/* Toolbar */}
                    <div className="cc-toolbar">
                        <button
                            type="button"
                            className="cc-add-lesson-btn"
                            onClick={() => {
                                const allExp = Object.values(expandedSemesters).every(v => v);
                                const nextState = !allExp;
                                const newState = {};
                                semestres.forEach(s => newState[s.id] = nextState);
                                setExpandedSemesters(newState);
                            }}
                        >
                            {Object.values(expandedSemesters).every(v => v)
                                ? "Contraer todas las secciones"
                                : "Expandir todas las secciones"}
                        </button>
                    </div>

                    {/* Lista de semestres (usando estructura de módulos) */}
                    <div className="cc-modules-list">
                        {semestres.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '3rem', background: '#f9fafb', borderRadius: '16px', border: '2px dashed #e5e7eb' }}>
                                <FiLayers style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem' }} />
                                <p style={{ color: '#6b7280' }}>Aún no hay niveles definidos.</p>
                                {!carrera.publicado && (
                                    <button
                                        className="cc-btn-secondary"
                                        style={{ marginTop: '1rem' }}
                                        onClick={handleAutoAddSemester}
                                    >
                                        Crear mi primer nivel
                                    </button>
                                )}
                            </div>
                        ) : (
                            semestres.map((semestre, idx) => {
                                const isExpanded = expandedSemesters[semestre.id];
                                const cursoCount = semestre.cursos.length;
                                const isEditingThisSemestre = editingSemestreId === semestre.id;

                                return (
                                    <div key={semestre.id} className="cc-module-card">
                                        {/* Header Semestre (Módulo) */}
                                        <div className="cc-module-header">
                                            <div className="cc-module-header-left">
                                                <div className="cc-module-index-badge">{idx + 1}</div>
                                                <div>
                                                    <div className="cc-module-title-wrapper">
                                                        {isEditingThisSemestre && !carrera.publicado ? (
                                                            <input
                                                                className="cc-inline-input"
                                                                value={semestreTitleDraft}
                                                                onChange={(e) => setSemestreTitleDraft(e.target.value)}
                                                                onBlur={() => saveSemestreTitle(semestre.id)}
                                                                onKeyDown={(e) => handleSemestreTitleKeyDown(e, semestre.id)}
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <h2
                                                                className="cc-module-title"
                                                                onDoubleClick={() => {
                                                                    if (carrera.publicado) return;
                                                                    setSemestreTitleDraft(semestre.nombre);
                                                                    setEditingSemestreId(semestre.id);
                                                                }}
                                                                title={carrera.publicado ? "Carrera publicada - Edición bloqueada" : "Doble clic para editar"}
                                                                style={{ cursor: carrera.publicado ? 'default' : 'text', color: 'var(--color-primary-dark)' }}
                                                            >
                                                                {semestre.nombre}
                                                            </h2>
                                                        )}
                                                    </div>
                                                    <p className="cc-module-meta">
                                                        {cursoCount} {cursoCount === 1 ? 'curso' : 'cursos'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="cc-module-header-right">
                                                {!carrera.publicado && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="cc-lesson-btn cc-lesson-btn-edit"
                                                            onClick={() => {
                                                                setClassicEditingSemesterId(semestre.id);
                                                                setSemesterName(semestre.nombre);
                                                                setSemesterDescription(semestre.descripcion || '');
                                                                setIsAddSemesterModalOpen(true);
                                                            }}
                                                            title="Editar nivel (modal)"
                                                        >
                                                            <FiEdit2 />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="cc-lesson-btn cc-lesson-btn-delete"
                                                            onClick={() => {
                                                                setDeleteDialog({ open: true, type: 'semestre', id: semestre.id, name: semestre.nombre });
                                                            }}
                                                            title="Eliminar nivel"
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    type="button"
                                                    className="cc-module-toggle-btn"
                                                    onClick={() => toggleSemester(semestre.id)}
                                                    title={isExpanded ? "Contraer" : "Expandir"}
                                                >
                                                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Contenido (Cursos) */}
                                        {isExpanded && (
                                            <div className="cc-module-content">
                                                {(semestre.cursos || []).map((curso) => (
                                                    <div key={curso.idSemestreCurso} className="cc-lesson-row">
                                                        <div className="cc-lesson-main">
                                                            <div className="cc-lesson-icon" style={{ padding: 0, overflow: 'hidden', width: 42, height: 42, borderRadius: 8 }}>
                                                                {curso.imagen ? (
                                                                    <img
                                                                        src={curso.imagen.startsWith('http') ? curso.imagen : `${SERVER_URL}${curso.imagen}`}
                                                                        alt=""
                                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    />
                                                                ) : (
                                                                    <FiBook />
                                                                )}
                                                            </div>
                                                            <div className="cc-lesson-texts">
                                                                <h3 className="cc-lesson-title" style={{ color: 'var(--color-primary-dark)' }}>{curso.nombre}</h3>
                                                                <p className="cc-lesson-meta">
                                                                    <span style={{ color: 'var(--color-primary-dark)', fontWeight: 600 }}>{curso.nivel}</span>
                                                                    <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                                                                    <span>{curso.habilidad}</span>
                                                                    <span style={{ margin: '0 8px', color: '#d1d5db' }}>|</span>
                                                                    <span>{curso.duracion} días</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="cc-lesson-actions">
                                                        </div>
                                                    </div>
                                                ))}
                                                {semestre.cursos.length === 0 && (
                                                    <div className="cc-empty-state">
                                                        <p>No hay cursos asignados a este nivel.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Botón agregar semestre - Estilo cc-add-module-btn */}
                    {!carrera.publicado && (
                        <div className="cc-add-module-wrapper">
                            <button
                                type="button"
                                className="cc-add-module-btn"
                                onClick={handleAutoAddSemester}
                            >
                                + Agregar nuevo nivel
                            </button>
                        </div>
                    )}
                </div>

                {/* SIDEBAR / Resumen */}
                <aside className="cc-sidebar">
                    <div className="cc-summary-card">
                        <h3 className="cc-summary-title" style={{ marginTop: 0 }}>
                            Estructura Académica
                        </h3>

                        <div className="cc-summary-grid">
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Niveles</small>
                                <strong className="cc-summary-value">{semestres.length}</strong>
                            </div>
                            <div className="cc-summary-item">
                                <small className="cc-summary-label">Cursos Totales</small>
                                <strong className="cc-summary-value">
                                    {semestres.reduce((acc, s) => acc + s.cursos.length, 0)}
                                </strong>
                            </div>
                        </div>

                        <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
                                {carrera?.descripcion || carrera?.Descripcion || 'Sin descripción disponible.'}
                            </p>
                        </div>

                        {/* Modal Configuración de Carrera (Inline) */}
                        {isCarreraModalOpen && (
                            <div className="cc-modal-overlay">
                                <div className="cc-course-modal">
                                    <h3 className="cc-course-modal-title">Configuración de la Carrera</h3>
                                    <p className="cc-course-modal-subtitle">
                                        Ajusta la información general de la carrera. Los cambios se reflejarán inmediatamente para los estudiantes.
                                    </p>

                                    <div className="cc-course-modal-body">
                                        <div className="cc-course-modal-field">
                                            <label>Nombre de la Carrera</label>
                                            <input
                                                type="text"
                                                value={careerConfigDraft.nombre}
                                                onChange={(e) => setCareerConfigDraft(prev => ({ ...prev, nombre: e.target.value }))}
                                                placeholder="Ej. Desarrollo Full Stack"
                                                disabled={carrera.publicado}
                                            />
                                        </div>

                                        <div className="cc-course-modal-field">
                                            <label>Descripción</label>
                                            <textarea
                                                value={careerConfigDraft.descripcion}
                                                onChange={(e) => setCareerConfigDraft(prev => ({ ...prev, descripcion: e.target.value }))}
                                                placeholder="Describe el objetivo y alcance de esta carrera..."
                                                disabled={carrera.publicado}
                                                style={{
                                                    width: '100%',
                                                    minHeight: '100px',
                                                    padding: '0.75rem',
                                                    borderRadius: '8px',
                                                    border: '1px solid #d1d5db',
                                                    fontFamily: 'inherit',
                                                    fontSize: '0.9rem',
                                                    backgroundColor: carrera.publicado ? '#f3f4f6' : 'white',
                                                    cursor: carrera.publicado ? 'not-allowed' : 'text'
                                                }}
                                            />
                                        </div>

                                        <div className="cc-course-modal-field">
                                            <label>Imagen de portada</label>
                                            {/* Área de carga con preview integrado - Estilo CourseCurriculum */}
                                            <div
                                                style={{
                                                    border: careerConfigDraft.urlImagen
                                                        ? '2px solid #10b981'
                                                        : `2px dashed ${configImageError ? '#ef4444' : '#cbd5e1'}`,
                                                    borderRadius: 8,
                                                    padding: '0.75rem',
                                                    backgroundColor: careerConfigDraft.urlImagen ? '#ecfdf5' : '#f9fafb',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                }}
                                            >
                                                {/* Badge de estado */}
                                                {careerConfigDraft.urlImagen && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '12px',
                                                            right: '12px',
                                                            backgroundColor: '#10b981',
                                                            color: 'white',
                                                            padding: '4px 12px',
                                                            borderRadius: '12px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        <span>✓</span>
                                                        <span>Archivo cargado</span>
                                                    </div>
                                                )}

                                                {/* Vista previa si existe */}
                                                {careerConfigDraft.urlImagen && (
                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <p style={{
                                                            fontSize: '0.85rem',
                                                            color: '#64748b',
                                                            marginBottom: '0.8rem',
                                                            fontWeight: 600
                                                        }}>
                                                            Vista previa:
                                                        </p>
                                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                            <img
                                                                src={careerConfigDraft.urlImagen.startsWith('http') ? careerConfigDraft.urlImagen : `${SERVER_URL}${careerConfigDraft.urlImagen}`}
                                                                alt="Vista previa"
                                                                style={{
                                                                    width: '100%',
                                                                    maxHeight: 180,
                                                                    borderRadius: 6,
                                                                    objectFit: 'cover',
                                                                    display: 'block',
                                                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Botón de carga */}
                                                <label
                                                    htmlFor="cc-career-image-input"
                                                    style={{
                                                        cursor: carrera.publicado ? 'not-allowed' : (configImageUploading ? 'wait' : 'pointer'),
                                                        width: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        padding: careerConfigDraft.urlImagen ? '0.5rem' : '0',
                                                        opacity: (configImageUploading || carrera.publicado) ? 0.6 : 1,
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleConfigImageChange}
                                                        style={{ display: 'none' }}
                                                        id="cc-career-image-input"
                                                        disabled={configImageUploading || carrera.publicado}
                                                    />
                                                    <div style={{ fontSize: '1.3rem', color: careerConfigDraft.urlImagen ? '#059669' : '#64748b' }}>
                                                        {configImageUploading ? '⏳' : careerConfigDraft.urlImagen ? '🔄' : '📷'}
                                                    </div>
                                                    <div style={{ color: careerConfigDraft.urlImagen ? '#059669' : '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                                                        {carrera.publicado
                                                            ? 'Imagen bloqueada - Carrera publicada'
                                                            : (configImageUploading ? 'Subiendo imagen...' : careerConfigDraft.urlImagen ? 'Cambiar imagen' : 'Haz clic para subir imagen')}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: careerConfigDraft.urlImagen ? '#059669' : '#94a3b8' }}>JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</div>
                                                </label>
                                            </div>

                                            {configImageError && (
                                                <small style={{ color: '#b91c1c', fontSize: '.8rem', display: 'block', marginTop: 4 }}>
                                                    {configImageError}
                                                </small>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={() => setIsCarreraModalOpen(false)}
                                        >
                                            Cancelar
                                        </button>
                                        {!carrera.publicado && (
                                            <button
                                                type="button"
                                                className="cc-btn-primary"
                                                onClick={handleSaveCareerConfig}
                                                disabled={!careerConfigDraft.nombre || configImageUploading}
                                            >
                                                Guardar Cambios
                                            </button>
                                        )}
                                        {carrera.publicado && (
                                            <p style={{ color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic', marginTop: '1rem', textAlign: 'center' }}>
                                                Información de carrera bloqueada por publicación.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <button
                            className="cc-summary-btn-main"
                            style={{ fontSize: '0.85rem' }}
                            onClick={() => setIsCarreraModalOpen(true)}
                        >
                            Editar configuración de la Carrera
                        </button>

                        {/* Botón publicar / solicitar revisión - Solo si no está publicada */}
                        {carrera && !carrera.publicado && (
                            <button
                                type="button"
                                className="cc-summary-btn-main"
                                style={{
                                    marginTop: '0.75rem',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }}
                                onClick={handlePrePublishValidation}
                            >
                                {hasCertificate ? '🚀 Publicar Carrera' : '📜 Solicitar Revisión'}
                            </button>
                        )}
                    </div>
                </aside>
            </div>

            {/* Alert Dialog para Publicar Carrera */}
            <AlertDialog
                open={isPublishAlertOpen}
                onClose={() => {
                    if (!isPublishing) handlePublishCarrera();
                }}
                onCancel={() => setIsPublishAlertOpen(false)}
                title="¿Publicar Carrera?"
                message={
                    isPublishing
                        ? "Generando certificado y publicando carrera. Por favor espere, esto puede tomar unos segundos..."
                        : "¿Estás seguro de que deseas publicar esta carrera? Ten en cuenta que una vez publicada, no podrás realizar ninguna modificación al contenido."
                }
                buttonText={isPublishing ? "Procesando..." : "Publicar"}
                cancelText="Cancelar"
                variant="warning"
            />

            {/* Modal Agregar/Editar Semestre */}
            {isAddSemesterModalOpen && (
                <div className="cc-modal-overlay">
                    <div className="cc-course-modal" style={{ maxWidth: classicEditingSemesterId ? 900 : 450, width: '90%' }}>
                        <h2 className="cc-course-modal-title">
                            {classicEditingSemesterId ? 'Gestionar Nivel' : 'Nuevo Nivel'}
                        </h2>

                        <div className="cc-course-modal-body" style={{ minHeight: classicEditingSemesterId ? 450 : 'auto' }}>
                            {classicEditingSemesterId ? (
                                <SemestreCursoTab
                                    fkValue={classicEditingSemesterId}
                                    initialName={semesterName}
                                    idCarrera={idCarrera}
                                    isReadOnly={carrera.publicado}
                                    onSaveSuccess={() => {
                                        setIsAddSemesterModalOpen(false);
                                        loadData();
                                    }}
                                />
                            ) : (
                                <div style={{ padding: '1.5rem' }}>
                                    <div className="cc-course-modal-field">
                                        <label>Nombre del Nivel</label>
                                        <input
                                            type="text"
                                            placeholder="Ej. Primer Nivel, Propedéutico..."
                                            value={semesterName}
                                            onChange={e => setSemesterName(e.target.value)}
                                            autoFocus
                                        />
                                        <small className="cc-course-modal-hint">Asegúrate de que el nombre sea claro para los estudiantes.</small>
                                    </div>

                                    <div className="cc-course-modal-actions" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                                        <button
                                            className="btn-cancel"
                                            onClick={() => {
                                                setIsAddSemesterModalOpen(false);
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                        <button className="cc-btn-primary" onClick={handleAutoAddSemester}>
                                            Crear Nivel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Agregar Curso al Semestre */}
            {isAddCourseModalOpen && (
                <div className="cc-modal-overlay">
                    <div className="cc-course-modal" style={{ maxWidth: 600, maxHeight: '80vh' }}>
                        <h2 className="cc-course-modal-title">Agregar Curso</h2>
                        <p className="cc-course-modal-subtitle">Selecciona un curso del catálogo para agregarlo a este nivel.</p>

                        <div style={{ padding: '0 1.5rem 1rem' }}>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    className="cc-input"
                                    placeholder="Buscar cursos..."
                                    style={{ paddingLeft: '2.5rem', width: '100%' }}
                                    value={searchCourse}
                                    onChange={e => setSearchCourse(e.target.value)}
                                />
                                <div style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>
                                    🔍
                                </div>
                            </div>
                        </div>

                        <div className="cc-course-modal-body" style={{ overflowY: 'auto', minHeight: '300px' }}>
                            {filteredAvailableCourses.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                                    <FiAlertCircle style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                                    <p>No se encontraron cursos disponibles.</p>
                                </div>
                            ) : (
                                filteredAvailableCourses.map(curso => (
                                    <div
                                        key={curso.idCurso || curso.IdCurso}
                                        className="cc-newcontent-option"
                                        style={{ padding: '12px 16px', gap: '12px' }}
                                        onClick={() => handleAddCourseToSemester(curso.idCurso || curso.IdCurso)}
                                    >
                                        <div className="cc-newcontent-icon-wrapper" style={{ width: 40, height: 40 }}>
                                            <FiBook className="cc-newcontent-icon" style={{ width: 20, height: 20 }} />
                                        </div>
                                        <div className="cc-newcontent-text">
                                            <div className="cc-newcontent-title" style={{ fontSize: '14px' }}>{curso.nombre || curso.Nombre}</div>
                                            <div className="cc-newcontent-subtitle" style={{ fontSize: '12px' }}>{curso.duracion || '0'} días • {curso.idNivel === 1 ? 'Básico' : curso.idNivel === 2 ? 'Intermedio' : 'Avanzado'}</div>
                                        </div>
                                        <div style={{ color: '#BE0032', fontSize: '1.2rem' }}><FiPlus /></div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="cc-course-modal-actions">
                            <button className="cc-btn-secondary" onClick={() => setIsAddCourseModalOpen(false)}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Diálogo de eliminación */}
            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, type: '', id: null, name: '' })}
                onConfirm={deleteDialog.type === 'semestre' ? confirmDeleteSemester : confirmDeleteCourse}
                title={deleteDialog.type === 'semestre' ? '¿Eliminar Nivel?' : '¿Quitar Curso?'}
                message={<>¿Estás seguro de que deseas eliminar <strong>{deleteDialog.name}</strong>? {deleteDialog.type === 'semestre' && 'Se perderán las asociaciones con cursos.'}</>}
            />

            {/* Modal de alertas estándar LMS */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                buttonText="Aceptar"
            />

            {/* Modal para Solicitar Revisión de Carrera */}
            <SolicitarRevisionCarreraModal
                open={isSolicitarModalOpen}
                onClose={() => setIsSolicitarModalOpen(false)}
                carreraId={idCarrera}
                carreraName={carrera?.nombre}
            />

            {/* Modal de Observaciones Administrativas */}
            <ObservacionesCarreraModal
                isOpen={isObservacionesModalOpen}
                onClose={() => setIsObservacionesModalOpen(false)}
                idCarrera={idCarrera}
                readOnly={true} // El instructor solo lee el feedback
            />

            {/* Botón flotante de observaciones */}
            <button
                className="floating-observaciones-btn"
                onClick={() => setIsObservacionesModalOpen(true)}
                title="Ver observaciones del administrador"
                style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    zIndex: 9999,
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
                <FiMessageSquare size={24} />
            </button>
        </div>
    );
};

export default CarreraCurriculum;
